import {
    useContext,
    useEffect,
    useState
} from 'react';
import {
    Col,
    Flex,
    Input,
    Modal,
    Row,
    Segmented
} from 'antd';
import {
    Error,
    Fieldset,
    Legend
} from '../styled-components/form';
import {
    Controller,
    SubmitHandler,
    useForm
} from 'react-hook-form';
import {
    SegmentEnum
} from '../constants';
import {
    useLazyQuery,
    useMutation
} from '@apollo/client';
import {
    NotificationContext
} from '../context/notification-provider';
import { useSelector } from "react-redux";
import { selectRestaurant } from '../store/selectors/user.selector';
import { RETRIEVE_USER_RESTAURANT_BY_ID } from '../apollo/query/user.query';
import { INSERT_USER_RESTAURANT, UPDATE_USER_RESTAURANT } from '../apollo/mutation/user.mutation';
import Password from 'antd/es/input/Password';

export interface PropTypes {
    id: number | null;
    visible: number | null;
    setVisible: (state: null | number) => void;

}

const UserDetail = (props: PropTypes) => {
    /** Retrieve component properties */
    const { id, visible, setVisible } = props;
    /** Retrieve the restaurant ID */
    const restaurant = useSelector(selectRestaurant);
    /** State to manage the tab selected */
    const [tab, setTab] = useState<string>(SegmentEnum.GeneralInformation);
    /** Notification utilities */
    const { showNotification } = useContext(NotificationContext);
    /** Formulary configuration */
    const { control, handleSubmit, formState, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            nID: null,
            sName: '',
            sSurname: '',
            nIDRestaurant: restaurant?.nID,
            sPassword: ''
        }
    });
    /** Manage to retrieve a category by id */
    const [ retrieveUserRestaurantById, { data } ] = useLazyQuery(RETRIEVE_USER_RESTAURANT_BY_ID);
    /** Manage to insert a category */
    const [ insertUserRestaurant, { data: responseInsertion } ] = useMutation(INSERT_USER_RESTAURANT, { refetchQueries: ['gqlRetrieveUsersRestaurant'] });
    /** Manage to update a category */
    const [ updateUserRestaurant, { data: responseUpdation } ] = useMutation(UPDATE_USER_RESTAURANT, { refetchQueries: ['gqlRetrieveUsersRestaurant'] });
    
    /** Manage to retrieve the information of the category if we have an id */
    useEffect((): void => {
        reset({
            nID: null,
            sName: '',
            sSurname: '',
            nIDRestaurant: restaurant?.nID,
            sPassword: ''
        });
        if ( id && id !== -1) {
            retrieveUserRestaurantById({ fetchPolicy: 'no-cache', variables: { nID: id, nIDRestaurant: restaurant?.nID } })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    
    /** Manage to initialize the formulary with the category retrieved */
    useEffect((): void => {
        if( data?.data ) {
            const { nID, sName, sSurname, sPassword } = data?.data;

            reset({ nID, sName, sSurname, sPassword, nIDRestaurant: restaurant?.nID });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    /** Manage to close the category modal */
    const onClose = (): void => {
        setTab(SegmentEnum.GeneralInformation);
        reset();
        setVisible(null);
    }

    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( responseInsertion?.code == 200 || responseUpdation?.code == 200 ) {
            showNotification({ severity: 'success', title: id && id !== -1 ? 'Usuari actualitzat!' : 'Nou usuari actualitzat' });
            onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responseInsertion, responseUpdation]);
    
    /** Manage to request the insertion and updation of a user */
    const onSubmit: SubmitHandler<any> = (data): void => {
        const { nID, ...rest } = data;

        if( id && id !== -1) {
            updateUserRestaurant({ variables: { nID: id, user: {...rest } } });
        } else {
            insertUserRestaurant({ variables: { user: { ...rest }} });
        }
    }
    
    return (
        <Modal
            title="Usuari"
            open={!!visible}
            onOk={handleSubmit(onSubmit)}
            okButtonProps={{ disabled: !formState.isValid }}
            onCancel={() => onClose()}
        >
            <Flex vertical>
                <Segmented
                    value={tab}
                    style={{ marginBottom: 8 }}
                    onChange={setTab}
                    options={[SegmentEnum.GeneralInformation]}
                />

                <form onSubmit={handleSubmit(onSubmit)}>
                    { tab === SegmentEnum.GeneralInformation && 
                        <Flex vertical gap={10}>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="sName"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Nom </Legend>
                                                <Input size="middle" style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="sSurname"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Cognom/s </Legend>
                                                <Input size="middle" style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="sPassword"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Contrasenya </Legend>
                                                <Password style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                        </Flex>
                    }
                </form>
            </Flex>
        </Modal>
    );
}

export default UserDetail;